import { Suspense, lazy, useEffect, type ComponentType } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { UNAUTHENTICATED_EVENT } from './api/client';
import { AppShell } from './app/AppShell';
import { NotFoundRoute } from './app/NotFound';
import { useSession } from './app/SessionProvider';
import { EmptyState, Spinner } from './components/ui';
import { FirstRunRoute } from './auth/FirstRunRoute';
import { LoginRoute } from './auth/LoginRoute';
import { ChatRoute } from './chat/ChatRoute';
import { zh } from './i18n';
import { MemoryRoute } from './memory/MemoryRoute';
import { SettingsRoute } from './settings/SettingsRoute';
import { SpaceDetailRoute } from './spaces/SpaceDetailRoute';
import { SpacesRoute } from './spaces/SpacesRoute';
import { ThreadListRoute } from './threads/ThreadListRoute';

/**
 * FROZEN SEAM (PLAN §5 / SPEC R20).
 *
 * `/admin/*` is discovered — never imported. `import.meta.glob` compiles to an
 * empty object when `src/admin/AdminRoutes.tsx` does not exist, so `web-chat`
 * builds and ships on its own and renders 「管理功能尚未安裝」; when `web-admin`
 * later adds that one file, the same build picks it up with no edit here. That
 * is what lets the two modules own strictly disjoint paths.
 *
 * `web-chat` must never create anything under `src/admin/`.
 */
interface AdminRoutesModule {
  readonly default: ComponentType;
}

const adminModules = import.meta.glob<AdminRoutesModule>('./admin/AdminRoutes.tsx');
const adminEntry = Object.values(adminModules)[0];
const AdminRoutes = adminEntry ? lazy(adminEntry) : null;

function AdminMount(): JSX.Element {
  const { isAdmin, isLoading } = useSession();

  if (isLoading) {
    return (
      <AppShell title={zh.tabs.admin}>
        <Spinner />
      </AppShell>
    );
  }

  // A member must not learn that an admin surface exists (CONTRACTS §2:
  // cross-tenant ids answer NOT_FOUND, not FORBIDDEN).
  if (!isAdmin) return <NotFoundRoute />;

  if (!AdminRoutes) {
    return (
      <AppShell title={zh.tabs.admin}>
        <EmptyState title={zh.admin.notInstalled} body={zh.admin.notInstalledHint} />
      </AppShell>
    );
  }

  return (
    <Suspense
      fallback={
        <AppShell title={zh.tabs.admin}>
          <Spinner />
        </AppShell>
      }
    >
      <AdminRoutes />
    </Suspense>
  );
}

function RequireAuth(): JSX.Element {
  const { me, isLoading, error } = useSession();
  const location = useLocation();

  if (isLoading && !me) {
    return (
      <AppShell>
        <Spinner />
      </AppShell>
    );
  }
  if (error || !me) return <Navigate to="/login" replace />;
  if (me.user.mustChangePassword && location.pathname !== '/first-run') {
    return <Navigate to="/first-run" replace />;
  }
  return <Outlet />;
}

export function AppRoutes(): JSX.Element {
  const navigate = useNavigate();

  // Any 401 anywhere (REST or the stream) bounces to /login once.
  useEffect(() => {
    const handler = (): void => {
      void navigate('/login', { replace: true });
    };
    window.addEventListener(UNAUTHENTICATED_EVENT, handler);
    return () => window.removeEventListener(UNAUTHENTICATED_EVENT, handler);
  }, [navigate]);

  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      <Route element={<RequireAuth />}>
        <Route path="/first-run" element={<FirstRunRoute />} />
        <Route path="/" element={<ThreadListRoute />} />
        <Route path="/c/:threadId" element={<ChatRoute />} />
        <Route path="/memory" element={<MemoryRoute />} />
        <Route path="/spaces" element={<SpacesRoute />} />
        <Route path="/spaces/:slug" element={<SpaceDetailRoute />} />
        <Route path="/settings" element={<SettingsRoute />} />
        <Route path="/admin/*" element={<AdminMount />} />
      </Route>
      <Route path="*" element={<NotFoundRoute />} />
    </Routes>
  );
}
